// @ts-check
// Página do modelo de WhatsApp (spec/whatsapp.md seção 3.7 C; contrato 09 de campanhas de WhatsApp, seção 2):
// situação com explicação (e atualização a cada 20 s enquanto a Meta revisa), categoria e o histórico dela,
// qualidade, números da mesma conta, prévia, campanhas e fluxos que usam, "Usar em nova campanha", mudar entre
// Comunicados e Atendimento e excluir com confirmação (a Meta bloqueia o nome por 30 dias).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate, searchParamsState } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { listChannels } from '../../data/conversations.js';
import { deleteWhatsappTemplate, getWhatsappTemplateDetail, updateWhatsappTemplateUsage } from '../../data/integrations.js';
import { ApiError } from '../../data/api-client.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { StatusBadge, describeError } from '../settings/integration-ui.js';
import { TemplateBadges, templateBadgeLabel, templateStatusLabel } from '../settings/whatsapp-templates/whatsapp-template-card.js';
import { WhatsappMessagePreview } from '../whatsapp-campaigns/whatsapp-message-preview.js';
import { CAMPAIGN_WHATSAPP_TEMPLATES_PATH, campaignTemplateHref, channelLabel, rejectedReasonDisplay } from '../../lib/whatsapp-templates.js';
import { readReturnPath } from '../../lib/return-path.js';
import { formatShortDate } from '../../lib/format.js';
import { isTemplateInReview, useReviewRefresh } from './use-review-refresh.js';
import { spaLinkHandler } from './whatsapp-templates-tab.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../data/conversations.js').Channel} Channel
 * @typedef {Awaited<ReturnType<typeof getWhatsappTemplateDetail>>} TemplateDetail
 */

/** Rascunho de campanha com o modelo (a lista das campanhas da 2B cria e abre o editor). @param {WhatsappTemplate} template */
export function newCampaignFromTemplateHref(template) {
  const parameters = new URLSearchParams({ new: '1', templateId: template.id, kind: template.category === 'MARKETING' ? 'MARKETING' : 'ANNOUNCEMENT' });
  return `/campaigns/whatsapp?${parameters}`;
}

/** @param {unknown} category */
const categoryName = (category) => (hasTranslation(`whatsappTemplates.categories.${category}`) ? t(`whatsappTemplates.categories.${category}`) : String(category ?? ''));

/** @param {{ templateId: string }} props */
export function WhatsappTemplateDetailPage({ templateId }) {
  const canManage = hasCurrentPermissionFlag('WORKSPACE') || hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS');
  const canOpenCampaigns = hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS');
  const canOpenFlows = hasCurrentPermissionFlag('WORKFLOWS');
  const [returnPath] = useState(() => readReturnPath(searchParamsState.value.get('from'), campaignTemplateHref(templateId)));
  const [detail, setDetail] = useState(/** @type {TemplateDetail | null} */ (null));
  const [channels, setChannels] = useState(/** @type {Channel[]} */ ([]));
  const [error, setError] = useState(/** @type {{ isNotFound: boolean, message: string } | null} */ (null));
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingUsage, setIsChangingUsage] = useState(false);

  const loadDetail = () => getWhatsappTemplateDetail(templateId).then((data) => {
    setDetail(data);
    setError(null);
  });
  const load = () => {
    setError(null);
    Promise.all([getWhatsappTemplateDetail(templateId), listChannels().catch(() => ({ channels: [] }))])
      .then(([data, channelData]) => {
        setDetail(data);
        setChannels(channelData.channels.filter((channel) => channel.type === 'WHATSAPP'));
      })
      .catch((requestError) => setError({
        isNotFound: requestError instanceof ApiError && (requestError.statusCode === 404 || requestError.code === 'RECORD_NOT_FOUND'),
        message: describeError(requestError),
      }));
  };
  useEffect(load, [templateId]);
  useEffect(() => {
    if (detail) document.title = `${detail.template.name} - ${t('campaignsBase.screens.templates')}`;
  }, [detail?.template.name]);
  useReviewRefresh(isTemplateInReview(detail?.template), () => loadDetail().catch(() => null));

  const listHref = CAMPAIGN_WHATSAPP_TEMPLATES_PATH;
  const hasOtherReturn = Boolean(returnPath && returnPath !== CAMPAIGN_WHATSAPP_TEMPLATES_PATH);
  const header = html`<${PageHeader} icon="IconBrandWhatsapp" title=${detail?.template.name ?? t('campaignsBase.screens.templates')} />`;
  const backLinks = html`<div class="templates-header-actions">
    <a class="integration-link wa-template-back" href=${listHref} onClick=${spaLinkHandler(listHref)}><${Icon} name="IconArrowLeft" size=${14} />${t('whatsappTemplates.page.back')}</a>
    ${hasOtherReturn && returnPath
      ? html`<a class="integration-link wa-template-back" href=${returnPath} onClick=${spaLinkHandler(returnPath)}><${Icon} name="IconArrowBackUp" size=${14} />${t('whatsappTemplates.campaignForm.goBack')}</a>`
      : null}
  </div>`;

  if (error) {
    return html`<${PageCard} className="campaigns-screen" header=${header}>
      <div class="wa-template-screen wa-template-page-main">
        ${backLinks}
        <div class="integration-empty templates-empty" role="alert">
          <p>${error.isNotFound ? t('whatsappTemplates.page.notFound') : t('whatsappTemplates.page.loadFailed')}</p>
          <p>${error.isNotFound ? t('whatsappTemplates.page.notFoundText') : error.message}</p>
          ${error.isNotFound ? null : html`<${Button} title=${t('whatsappTemplates.actions.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} />`}
        </div>
      </div>
    <//>`;
  }
  if (!detail) {
    return html`<${PageCard} className="campaigns-screen" header=${header}>
      <div class="wa-template-screen wa-template-page-main" aria-busy="true">${backLinks}<${Skeleton} height=${44} radius=${8} /><${Skeleton} height=${200} radius=${8} /></div>
    <//>`;
  }

  const { template, copies, uses } = detail;
  const channelsById = new Map(channels.map((channel) => [channel.id, channel]));
  const numbers = [...new Set(copies.map((copy) => channelLabel(channelsById.get(copy.channelId)) || copy.channelId))];
  const reason = rejectedReasonDisplay(template.rejectedReason);
  const changeBadge = (template.correctCategory && template.correctCategory !== template.category)
    ? { key: template.categoryChangeAt ? 'category.willChange' : 'category.willChangeSoon', params: { category: template.correctCategory, date: template.categoryChangeAt } }
    : null;
  const totalUses = uses.campaigns.length + uses.flows.length;
  const canUseInCampaign = canOpenCampaigns && template.status === 'APPROVED' && template.isSendable && (template.group === 'MARKETING' || template.group === 'ANNOUNCEMENT');

  const changeUsage = async (/** @type {'CAMPAIGN' | 'CONVERSATION'} */ usage) => {
    setIsChangingUsage(true);
    try {
      await updateWhatsappTemplateUsage(template.id, usage);
      showSuccessSnackbar(t(usage === 'CAMPAIGN' ? 'whatsappTemplates.usage.changedToAnnouncement' : 'whatsappTemplates.usage.changedToService'));
      await loadDetail();
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setIsChangingUsage(false);
    }
  };
  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteWhatsappTemplate(template.id);
      showSuccessSnackbar(t('whatsappTemplates.deleteConfirm.deleted'));
      navigate(listHref);
    } catch (requestError) {
      showErrorSnackbar(requestError);
      setIsDeleteOpen(false);
      await loadDetail().catch(() => null);
    } finally {
      setIsDeleting(false);
    }
  };

  /** @param {{ id: string, name: string }} use @param {'CAMPAIGN' | 'FLOW'} kind */
  const useItem = (use, kind) => {
    const href = kind === 'FLOW' ? `/campaigns/flows/${encodeURIComponent(use.id)}` : `/campaigns/whatsapp/${encodeURIComponent(use.id)}`;
    const canOpen = kind === 'FLOW' ? canOpenFlows : canOpenCampaigns;
    return html`<li key=${`${kind}-${use.id}`}>
      ${canOpen ? html`<a class="integration-link" href=${href} onClick=${spaLinkHandler(href)}>${use.name}</a>` : html`<span>${use.name}</span>`}
    </li>`;
  };

  return html`<${PageCard} className="campaigns-screen" header=${header}>
    <div class="wa-template-screen wa-template-page">
      <div class="wa-template-page-main">
        ${backLinks}
        <div class="wa-detail-header">
          <h2 class="wa-detail-name">${template.name}</h2>
          <${StatusBadge} status=${template.status} label=${templateStatusLabel(template.status)} />
        </div>
        <${TemplateBadges} template=${template} />
        <p class="wa-notice" data-tone=${template.status === 'REJECTED' || template.status === 'DISABLED' ? 'danger' : 'info'}>
          <${Icon} name="IconInfoCircle" size=${16} />
          <span class="wa-notice-body">
            <span>${hasTranslation(`whatsappTemplates.page.statusExplain.${template.status}`) ? t(`whatsappTemplates.page.statusExplain.${template.status}`) : templateStatusLabel(template.status)}</span>
            ${isTemplateInReview(template) ? html`<span>${t('whatsappTemplates.page.inReviewRefresh')}</span>` : null}
            ${template.status === 'REJECTED'
              ? html`<span><strong>${t('whatsappTemplates.card.rejectedReason')}</strong> ${t(reason.key, { code: reason.code })}${reason.showCode ? html` <code class="template-card-code">${reason.code}</code>` : null}</span>`
              : null}
          </span>
        </p>
        <dl class="wa-detail-list">
          <dt>${t('whatsappTemplates.page.category')}</dt>
          <dd>
            <span>${[categoryName(template.category), t(`whatsappTemplates.groups.${template.group ?? 'OTHER'}`)].filter((label, index, labels) => labels.indexOf(label) === index).join(' · ')}</span>
            <span class="integration-muted-small">${t(`whatsappTemplates.groupHelp.${template.group ?? 'OTHER'}`)}</span>
          </dd>
          <dt>${t('whatsappTemplates.page.categoryHistory')}</dt>
          <dd>
            ${template.previousCategory && template.categoryChangedAt
              ? html`<span>${t('whatsappTemplates.category.changed', { previous: categoryName(template.previousCategory), category: categoryName(template.category), date: formatShortDate(template.categoryChangedAt) })}</span>`
              : null}
            ${changeBadge ? html`<span>${templateBadgeLabel(changeBadge)}</span>` : null}
            ${!changeBadge && !(template.previousCategory && template.categoryChangedAt) ? html`<span>${t('whatsappTemplates.page.categoryHistoryEmpty')}</span>` : null}
          </dd>
          <dt>${t('whatsappTemplates.page.quality')}</dt>
          <dd>${template.qualityScore ? t(`whatsappTemplates.quality.${template.qualityScore}`) : t('whatsappTemplates.page.qualityUnknown')}</dd>
          <dt>${t('whatsappTemplates.page.numbers')}</dt>
          <dd>${numbers.map((number) => html`<span key=${number}>${number}</span>`)}</dd>
          <dt>${t('whatsappTemplates.page.usedBy')}</dt>
          <dd>
            ${totalUses === 0
              ? html`<span>${t('whatsappTemplates.page.usedByEmpty')}</span>`
              : html`<span>${t('whatsappTemplates.page.usedByCount', { count: totalUses })}</span>
                  <ul class="wa-detail-uses">
                    ${uses.campaigns.map((use) => useItem(use, 'CAMPAIGN'))}
                    ${uses.flows.map((use) => useItem(use, 'FLOW'))}
                  </ul>`}
          </dd>
        </dl>
        <div class="wa-form-actions">
          ${canUseInCampaign
            ? html`<${Button} title=${t('whatsappTemplates.page.useInCampaign')} icon="IconSend" accent="blue" onClick=${() => navigate(newCampaignFromTemplateHref(template))} />`
            : null}
          ${canManage && template.group === 'SERVICE'
            ? html`<${Button} title=${t('whatsappTemplates.usage.toAnnouncement')} icon="IconSpeakerphone" variant="secondary" isLoading=${isChangingUsage} onClick=${() => changeUsage('CAMPAIGN')} />`
            : null}
          ${canManage && template.group === 'ANNOUNCEMENT'
            ? html`<${Button} title=${t('whatsappTemplates.usage.toService')} icon="IconArrowBackUp" variant="secondary" isLoading=${isChangingUsage} onClick=${() => changeUsage('CONVERSATION')} />`
            : null}
          ${canManage && (template.effectiveUsage === 'CAMPAIGN' || hasCurrentPermissionFlag('WORKSPACE'))
            ? html`<${Button} title=${t('whatsappTemplates.page.delete')} icon="IconTrash" variant="secondary" accent="danger" onClick=${() => setIsDeleteOpen(true)} />`
            : null}
        </div>
      </div>
      <aside class="wa-template-page-aside">
        <${WhatsappMessagePreview} title=${t('whatsappTemplates.page.preview')}
          template=${{ headerText: template.headerText, bodyText: template.bodyText, footerText: template.footerText, buttons: template.buttons ?? [] }} />
      </aside>
    </div>
    ${isDeleteOpen
      ? html`<${Modal} title=${t('whatsappTemplates.page.deleteTitle', { name: template.name })}
          subtitle=${totalUses > 0 ? t('errors.WHATSAPP_TEMPLATE_IN_USE') : t('whatsappTemplates.page.deleteText')}
          confirmLabel=${t('whatsappTemplates.deleteConfirm.confirm')} confirmAccent="danger" isConfirmDisabled=${totalUses > 0} isLoading=${isDeleting}
          onClose=${() => setIsDeleteOpen(false)} onConfirm=${confirmDelete} />`
      : null}
  <//>`;
}
